import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useAuth } from "@/lib/auth";
import { useGetChildrenQuery } from "@/query/apiSlice";
import type { RootState } from "@/store";

import { onResetSelectedChild, onSelectChild } from "./selectedChildSlice";

export function useSelectedChild() {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();
  const childId = useSelector(
    (state: RootState) => state.selectedChild.childId,
  );
  const {
    data: children = [],
    isLoading,
    isFetching,
  } = useGetChildrenQuery(undefined, {
    skip: !isAuthenticated,
  });

  const selectedChild = useMemo(
    () => children.find((child) => child.id === childId),
    [children, childId],
  );

  useEffect(() => {
    if (children.length === 0) {
      if (childId !== null) {
        dispatch(onResetSelectedChild());
      }
      return;
    }

    const hasValidSelection =
      childId !== null && children.some((child) => child.id === childId);

    if (!hasValidSelection) {
      dispatch(onSelectChild(children[0].id));
    }
  }, [dispatch, childId, children]);

  const selectChild = useCallback(
    (id: number) => {
      dispatch(onSelectChild(id));
    },
    [dispatch],
  );

  return {
    childId,
    selectedChild,
    children,
    // Only the initial load — do not treat background refetch (e.g. refetchOnFocus)
    // as loading, or screens that unmount on isLoading will reset local form state.
    isLoading,
    isFetching,
    selectChild,
  };
}
